import React from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
// should prevent all dayjs relative time error
dayjs.extend(relativeTime);

// Import the customParseFormat and advancedFormat plugins if you need more formatting options
import customParseFormat from "dayjs/plugin/customParseFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";

// Extend dayjs with the plugins
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

/**
 * Formats a date similar to Moment.js with the format "MMMM, DD, YYYY  — HH:mm a".
 * @param {Date|string|number} date - The date to format. Can be a Date object, a date string, or a timestamp.
 * @returns {string} - The formatted date string.
 */
const moment = (date: Date | string | number): string => {
  if (!(date instanceof Date)) {
    date = new Date(date);
  }

  const monthNames: string[] = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const day: string = date.getDate().toString().padStart(2, "0");
  const month: string = monthNames[date.getMonth()];
  const year: number = date.getFullYear();

  let hours: number = date.getHours();
  const minutes: string = date.getMinutes().toString().padStart(2, "0");
  const ampm: string = hours >= 12 ? "pm" : "am";

  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  const strTime = `${hours.toString().padStart(2, "0")}:${minutes} ${ampm}`;

  return `${month}, ${day}, ${year}  — ${strTime}`;
};

// Example usage

const formatDate = (date: Date | string | number): string => {
  const actualDate = String(moment(date));
  const splitDate: string[] = actualDate.split(",");
  const newDate: string =
    getOrdinalSuffix(splitDate[1]) +
    " " +
    splitDate[0].slice(0, 3) +
    splitDate[2]?.replace("—", ",");
  return newDate;
};

/**
 * Adds an ordinal suffix to a given day.
 * @param {string} day - The day to add the suffix to.
 * @returns {string} - The day with the ordinal suffix.
 */
const getOrdinalSuffix = (day: string): string => {
  const intDay: number = parseInt(day.trim(), 10);
  if (intDay > 3 && intDay < 21) return `${intDay}th`; // special case
  switch (intDay % 10) {
    case 1:
      return `${intDay}st`;
    case 2:
      return `${intDay}nd`;
    case 3:
      return `${intDay}rd`;
    default:
      return `${intDay}th`;
  }
};

export interface DateTimeProp {
  date: string | Date;
  hideTime?: boolean;
  hideAgo?: boolean;
  hideSmallDate?: boolean;
  className?: string;
  parentStyles?: React.CSSProperties;
}

const RavenDateTime: React.FC<DateTimeProp> = ({
  date,
  hideTime,
  hideAgo,
  hideSmallDate,
  className,
  parentStyles,
}: DateTimeProp) => {
  const hideTimeFunc = (param: string): string | undefined => {
    if (param) {
      const val: string[] = param?.split(",");
      return `${val[0]}`;
    }
    return "";
  };

  const formatDateToRelativeTime = (date: string): string => {
    if (date) {
      return dayjs()?.to(dayjs(date)) || "";
    }
    return "";
  };

  const capitalizeFirstLetter = (str?: string): string => {
    if (str) {
      const capitalizedStr = str.charAt(0).toUpperCase() + str.slice(1);
      return capitalizedStr;
    }
    return "";
  };

  return (
    <span
      style={parentStyles}
      className={`date-time-fragment-box-wrap ${className || ""}`}
    >
      {date && (
        <>
          {!hideAgo && (
            <span className="date">
              {capitalizeFirstLetter(formatDateToRelativeTime(date as string))}
            </span>
          )}
          {!hideSmallDate && (
            <span className="date-two date-time-fragment-box-wrap-date-two grey-color-white">
              {hideTime ? hideTimeFunc(formatDate(date)) : formatDate(date)}
            </span>
          )}
        </>
      )}
    </span>
  );
};

export default RavenDateTime;
